/**
 * /wireline-slickline/rates/new and /wireline-slickline/rates/:id — one rate on
 * the Wireline & Slickline rate card. Full pages, no dialogs; the delete is confirmed inline.
 *
 * Deleting a rate leaves every ticket line already priced from it alone (the
 * line keeps its copied rate, code and name) — the confirm says so.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWlCustomers, useWlMeta, useWlUnits } from '../../../hooks/useWirelineSlickline';
import { useCreateWlRate, useDeleteWlRate, useWlRate, useWlRates, useUpdateWlRate } from '../../../hooks/useWlTickets';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canWriteWlTickets, wlTicketRefusal } from '../../../lib/wireline-slickline-tickets';
import { wlCatalogLabel } from '../../../lib/wireline-slickline';
import { wlDay } from '../../../lib/wireline-slickline-jobs';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WL_EYEBROW, WlEmpty, WlLoading, WlPanel, WlSpec } from '../WlBits';
import { PfInlineConfirm as WlInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { WlRateForm, type WlRateFormOptions } from './RateForm';
import { wlRateAppliesTo } from './RatesScreen';
import { wlRatePriceText } from './TicketBits';
import type { WlTicketCatalog } from '../../../types/wireline-slickline-tickets';

function useRateFormOptions(enabled: boolean): WlRateFormOptions {
  const customers = useWlCustomers(enabled).data ?? [];
  // The unit list is read whenever the form is open; a refusal leaves it empty ("Any unit" still saves).
  const units = (useWlUnits().data?.data ?? []).map((u) => ({ id: u.id, unitNumber: u.unitNumber }));
  return { customers, units };
}

export function WlNewRateScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/wireline-slickline/rates');
  const metaRead = useWlMeta();
  const catalog = (metaRead.data?.data ?? null) as WlTicketCatalog | null;
  const metaQuery = useWlRates();
  const options = useRateFormOptions(true);
  const create = useCreateWlRate();
  const [error, setError] = useState<string | null>(null);
  const meta = metaQuery.data?.meta ?? null;
  return (
    <div data-testid="wl-rate-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="New rate" description="One line on the rate card." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <WlPanel eyebrow="Rate card" title="New rate">
            <WlRateForm
              catalog={catalog}
              options={options}
              currency={meta?.currency ?? null}
              depthUnit={metaRead.data?.meta.units?.depth ?? null}
              today={meta?.today ?? null}
              defaultCustomerId={searchParams.get('customerId') ?? ''}
              pending={create.isPending}
              error={error}
              submitLabel="Add rate"
              onCancel={back}
              onSubmit={(body) => {
                setError(null);
                create.mutate(body, {
                  onSuccess: (r) => {
                    toast.success('Rate added');
                    navigate(r.data ? `/wireline-slickline/rates/${r.data.id}` : '/wireline-slickline/rates', { replace: true });
                  },
                  onError: (e) => setError(wlTicketRefusal(e, 'The rate could not be added.')),
                });
              }}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}

export default function WlRateDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/rates');
  const { user } = useAuth();
  const canWrite = canWriteWlTickets(user?.role);
  const query = useWlRate(id);
  const catalog = (useWlMeta().data?.data ?? null) as WlTicketCatalog | null;
  const metaQuery = useWlRates(undefined, canWrite);
  const update = useUpdateWlRate();
  const del = useDeleteWlRate();
  const [editing, setEditing] = useState(false);
  const options = useRateFormOptions(canWrite && editing);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const r = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="wl-rate-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={r?.code || (query.isLoading ? 'Rate' : '—')}
        description={r ? `${r.name} · ${r.customerId ? r.customerName ?? '—' : 'Default card'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          r && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading rate" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !r) ? (
            <WlPanel eyebrow="Rate card" title="Rate">
              <WlEmpty>This rate was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !r ? (
            <WlPanel eyebrow="Rate card" title="Rate">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this rate" />
            </WlPanel>
          ) : (
            <>
              {deleting && (
                <WlInlineConfirm
                  testId="wl-rate-delete-confirm"
                  message={`Delete ${r.code} from the rate card? Ticket lines already priced from it keep their price. If a rent-in bills at this rate, its later tickets draft that line unpriced until it is given another billing rate.`}
                  confirmLabel="Delete rate"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(r.id, {
                      onSuccess: () => {
                        toast.success('Rate deleted');
                        navigate('/wireline-slickline/rates', { replace: true });
                      },
                      onError: (e) => setDeleteError(wlTicketRefusal(e, 'The rate could not be deleted.')),
                    })
                  }
                />
              )}
              {editing && canWrite ? (
                <WlPanel eyebrow="Edit" title={`Edit ${r.code}`}>
                  <WlRateForm
                    rate={r}
                    catalog={catalog}
                    options={options}
                    currency={r.currency}
                    depthUnit={r.depthUnit}
                    distanceUnit={r.distanceUnit}
                    today={metaQuery.data?.meta.today ?? null}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save rate"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      update.mutate(
                        { id: r.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Rate saved');
                            setEditing(false);
                          },
                          onError: (e) => setEditError(wlTicketRefusal(e, 'The rate could not be saved.')),
                        },
                      );
                    }}
                  />
                </WlPanel>
              ) : (
                <WlPanel eyebrow="Rate card" title="Details" testId="wl-rate-details">
                  <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                    <WlSpec label="Code" value={r.code || '—'} mono />
                    <WlSpec label="Name" value={r.name || '—'} />
                    <WlSpec label="Card" value={r.customerId ? r.customerName || '—' : 'Default card'} />
                    <WlSpec label="Kind" value={r.kindLabel || wlCatalogLabel(catalog?.priceKinds, r.kind)} />
                    <WlSpec label={r.kind === 'third_party' ? 'Pricing' : 'Rate'} value={wlRatePriceText(r)} mono />
                    <WlSpec label="Unit" value={r.unitLabel || wlCatalogLabel(catalog?.priceUnits, r.unit)} />
                    {r.depthUnit && <WlSpec label="Depth unit" value={r.depthUnit} />}
                    {r.distanceUnit && <WlSpec label="Distance unit" value={r.distanceUnit} />}
                    <WlSpec label="Currency" value={r.currency || '—'} />
                    <WlSpec label="Applies to" value={wlRateAppliesTo(r, catalog)} />
                    <WlSpec label="Starts" value={wlDay(r.effectiveFrom)} />
                    <WlSpec label="Ends" value={r.effectiveTo ? wlDay(r.effectiveTo) : 'No end'} />
                  </dl>
                  {(r.description || r.notes) && (
                    <div className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                      {r.description && <p>{r.description}</p>}
                      {r.notes && <p className="text-fieldiq-muted">{r.notes}</p>}
                    </div>
                  )}
                </WlPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
